/**
 * COBJ / COLOR OBJECT SHOPIFY THEME — Main JavaScript
 * Premium motion system: GSAP + ScrollTrigger + Lenis smooth scroll.
 * Fullscreen hero with layered parallax, slow fades & reveals,
 * image expansion on scroll, subtle hover + cursor interactions.
 */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var isDesktop = window.matchMedia('(min-width: 768px)').matches;
  var lenis = null;

  /* Shared "premium" motion values — slow and smooth everywhere */
  var EASE_OUT = 'power3.out';
  var EASE_SOFT = 'power2.out';
  var DUR_SLOW = 1.6;
  var DUR_BASE = 1.2;

  /* ============================================================
     1. LENIS SMOOTH SCROLL (synced with ScrollTrigger)
  ============================================================ */
  function initLenis() {
    if (reduceMotion || typeof Lenis === 'undefined' || typeof gsap === 'undefined') return;

    lenis = new Lenis({
      duration: 1.6,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      wheelMultiplier: 0.9,
      touchMultiplier: 1.4,
    });

    if (typeof ScrollTrigger !== 'undefined') {
      lenis.on('scroll', ScrollTrigger.update);
    }
    gsap.ticker.add(function (time) {
      lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);

    // Route in-page anchors through Lenis for a long, smooth glide
    document.querySelectorAll('a[href^="#"]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        var hash = link.getAttribute('href');
        if (!hash || hash.length < 2) return;
        var target = document.querySelector(hash);
        if (!target) return;
        e.preventDefault();
        lenis.scrollTo(target, { duration: 2, easing: function (t) { return 1 - Math.pow(1 - t, 4); } });
      });
    });
  }

  /* ============================================================
     2. DIGITAL CLOCK
  ============================================================ */
  function initClock() {
    const hourEl = document.querySelector('.hour');
    const minuteEl = document.querySelector('.minute');
    const colonEl = document.querySelector('.colon');
    if (!hourEl || !minuteEl) return;

    function updateTime() {
      const now = new Date();
      hourEl.textContent = String(now.getHours()).padStart(2, '0');
      minuteEl.textContent = String(now.getMinutes()).padStart(2, '0');
    }
    updateTime();
    setInterval(updateTime, 10000);

    if (typeof gsap !== 'undefined' && colonEl) {
      gsap.to(colonEl, {
        opacity: 0,
        duration: 0.6,
        ease: 'power1.inOut',
        repeat: -1,
        yoyo: true,
      });
    }
  }

  /* ============================================================
     3. CUSTOM MENU TOGGLE
  ============================================================ */
  function initMenu() {
    const menuToggle = document.querySelector('.custom-menu');
    const navMenu = document.querySelector('.custom-nav-menu');
    const bgGrow = document.querySelector('.bg-grow');
    const menuBlocker = document.querySelector('.menu-blocker');
    if (!menuToggle || !navMenu) return;

    let isOpen = false;

    function openMenu() {
      isOpen = true;
      navMenu.classList.add('is-open');
      menuToggle.classList.add('is-active');
      menuToggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('menu-open');
      if (typeof gsap !== 'undefined') {
        gsap.to(navMenu, { opacity: 1, visibility: 'visible', duration: 0.8, ease: EASE_OUT });
        if (bgGrow) gsap.fromTo(bgGrow, { scaleY: 0 }, { scaleY: 1, duration: 0.9, ease: 'power4.out' });
        gsap.fromTo(navMenu.querySelectorAll('.nav-link-1'),
          { yPercent: 60, opacity: 0 },
          { yPercent: 0, opacity: 1, duration: 1, ease: EASE_OUT, stagger: 0.06, delay: 0.15 }
        );
      }
    }

    function closeMenu() {
      isOpen = false;
      menuToggle.classList.remove('is-active');
      menuToggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('menu-open');
      if (typeof gsap !== 'undefined') {
        gsap.to(navMenu, {
          opacity: 0,
          duration: 0.5,
          ease: 'power2.in',
          onComplete: () => navMenu.classList.remove('is-open'),
        });
        if (bgGrow) gsap.to(bgGrow, { scaleY: 0, duration: 0.6, ease: 'power3.in' });
      } else {
        navMenu.classList.remove('is-open');
      }
    }

    menuToggle.addEventListener('click', () => (isOpen ? closeMenu() : openMenu()));
    menuToggle.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      isOpen ? closeMenu() : openMenu();
    });
    if (menuBlocker) {
      menuBlocker.addEventListener('click', (e) => {
        e.stopPropagation();
        if (isOpen) closeMenu();
      });
    }

    // Close on nav link click
    navMenu.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', closeMenu);
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen) closeMenu();
    });
  }

  /* ============================================================
     5. IMAGE TRAIL — cursor-following editorial image echoes
  ============================================================ */
  function initImageTrail() {
    if (reduceMotion) return;

    document.querySelectorAll('[data-image-trail]').forEach(function (section) {
      var stage = section.querySelector('.image-trail-stage');
      var cards = Array.from(section.querySelectorAll('.image-trail-card'));
      if (!stage || !cards.length) return;

      var strength = parseInt(section.getAttribute('data-trail-strength') || '3', 10);
      var cardWidth = cards[0].getBoundingClientRect().width || 200;
      // Require the cursor to travel roughly a full card width plus a 10%
      // gap before the next image appears, so consecutive images don't
      // stack on top of each other.
      var minDistance = cardWidth * 1.1;
      var index = 0;
      var lastX = 0;
      var lastY = 0;
      var hasMoved = false;

      function showCard(x, y) {
        var card = cards[index % cards.length];
        var rect = stage.getBoundingClientRect();
        var localX = x - rect.left;
        var localY = y - rect.top;
        var rotation = ((index % 7) - 3) * (2 + strength);

        card.style.zIndex = String(10 + index);

        if (typeof gsap !== 'undefined') {
          gsap.killTweensOf(card);
          gsap.fromTo(
            card,
            {
              x: localX,
              y: localY,
              xPercent: -50,
              yPercent: -50,
              scale: 0.76,
              rotate: rotation * -0.4,
              opacity: 0,
            },
            {
              scale: 1,
              rotate: rotation,
              opacity: 0.92,
              duration: 0.34,
              ease: 'power3.out',
              onComplete: function () {
                gsap.to(card, {
                  opacity: 0,
                  scale: 0.88,
                  y: localY - 18 - strength * 4,
                  duration: 0.9,
                  delay: 0.12,
                  ease: 'power2.inOut',
                });
              },
            }
          );
        } else {
          card.style.left = localX + 'px';
          card.style.top = localY + 'px';
          card.style.opacity = '0.85';
          window.setTimeout(function () {
            card.style.opacity = '0';
          }, 420);
        }

        index += 1;
      }

      section.addEventListener('mousemove', function (e) {
        if (!hasMoved) {
          hasMoved = true;
          lastX = e.clientX;
          lastY = e.clientY;
          showCard(e.clientX, e.clientY);
          return;
        }

        var dx = e.clientX - lastX;
        var dy = e.clientY - lastY;
        if (Math.sqrt(dx * dx + dy * dy) < minDistance) return;

        lastX = e.clientX;
        lastY = e.clientY;
        showCard(e.clientX, e.clientY);
      });

      section.addEventListener('mouseleave', function () {
        hasMoved = false;
        if (typeof gsap !== 'undefined') {
          gsap.to(cards, { opacity: 0, duration: 0.35, ease: 'power2.out' });
        }
      });

      // Touch devices have no cursor, so drive the same reveal by
      // dragging a finger across the section instead.
      section.addEventListener(
        'touchmove',
        function (e) {
          var touch = e.touches[0];
          if (!touch) return;

          if (!hasMoved) {
            hasMoved = true;
            lastX = touch.clientX;
            lastY = touch.clientY;
            showCard(touch.clientX, touch.clientY);
            return;
          }

          var dx = touch.clientX - lastX;
          var dy = touch.clientY - lastY;
          if (Math.sqrt(dx * dx + dy * dy) < minDistance) return;

          lastX = touch.clientX;
          lastY = touch.clientY;
          showCard(touch.clientX, touch.clientY);
        },
        { passive: true }
      );

      section.addEventListener('touchend', function () {
        hasMoved = false;
        if (typeof gsap !== 'undefined') {
          gsap.to(cards, { opacity: 0, duration: 0.35, ease: 'power2.out' });
        }
      });
    });
  }

  /* ============================================================
     6. GSAP ANIMATIONS (requires GSAP + ScrollTrigger)
  ============================================================ */
  function initGSAP() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

    gsap.registerPlugin(ScrollTrigger);
    if (reduceMotion) return;

    initHero();
    initServiceHover();
    initPortfolioLines();
    initReveals();
    initImageExpansion();
    initTestimonialsScroll();
    initCtaMarquee();

    // Re-measure once everything (fonts, images) has loaded
    window.addEventListener('load', function () {
      ScrollTrigger.refresh();
    });
  }

  /* ============================================================
     6. HERO — fullscreen, layered collage, parallax depth
  ============================================================ */

  /* Layer layout: offsets (viewport units), scale and depth.
     Depth drives both scroll parallax speed and mouse drift,
     so nearer layers move more — that's what sells the depth. */
  var HERO_LAYERS = [
    { x: '0vw',   y: '0vh',   scale: 1.35, depth: 0.3 },  // main, centered
    { x: '-30vw', y: '-4vh',  scale: 0.72, depth: 0.65 },
    { x: '29vw',  y: '7vh',   scale: 0.66, depth: 0.9 },
    { x: '-21vw', y: '13vh',  scale: 0.5,  depth: 1.25 },
    { x: '22vw',  y: '-12vh', scale: 0.55, depth: 1.1 },
    { x: '-9vw',  y: '-15vh', scale: 0.8,  depth: 1.5 },
  ];

  function initHero() {
    var section = document.querySelector('.hero-section');
    if (!section) return;

    var title = section.querySelector('.entrance-tittle .text-xxl');
    var wrappers = gsap.utils.toArray(section.querySelectorAll('.image-hero-wrapper'));
    var blurCard = section.querySelector('.area-blur-card');

    // The display serif runs wider than the mono the sizes were tuned for —
    // scale the title down until it fits the viewport, whatever the text.
    function fitTitle() {
      if (!title) return;
      var max = Math.min(section.clientWidth, window.innerWidth) * 0.92;
      title.style.fontSize = '';
      var natural = title.scrollWidth;
      if (natural > max) {
        title.style.fontSize = (parseFloat(getComputedStyle(title).fontSize) * max / natural) + 'px';
      }
    }
    fitTitle();
    window.addEventListener('resize', fitTitle);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        fitTitle();
        if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
      });
    }

    var tl = gsap.timeline({ defaults: { ease: EASE_OUT } });

    // --- Title: masked line reveal (per-char if SplitText is available) ---
    if (title) {
      if (typeof SplitText !== 'undefined') {
        var split = new SplitText(title, { type: 'chars' });
        gsap.set(title, { opacity: 1 });
        tl.from(split.chars, {
          yPercent: 110,
          opacity: 0,
          duration: DUR_SLOW,
          ease: 'power4.out',
          stagger: 0.045,
        }, 0.2);
      } else {
        tl.from(title, { yPercent: 110, opacity: 0, duration: 1.8, ease: 'power4.out' }, 0.2);
      }
    }

    // --- Side tags fade in late and slow ---
    tl.from(section.querySelectorAll('.hero-left-col, .hero-right-col'), {
      opacity: 0,
      y: 24,
      duration: DUR_BASE,
      stagger: 0.15,
    }, 0.9);

    if (!wrappers.length) return;

    if (wrappers.length > 1) {
      initHeroCollage(section, wrappers, tl, !isDesktop);
    } else {
      initHeroSlideshow(wrappers, tl);
    }

    // The blur veil resolves away as the hero settles — a soft "focus" reveal
    if (blurCard) {
      tl.to(blurCard, { opacity: 0, duration: 2, ease: EASE_SOFT }, 1.1);
    }
  }

  /* Desktop: scatter the hero slots into a layered collage with
     per-layer scroll parallax and mouse drift. */
  function initHeroCollage(section, wrappers, tl, mobile) {
    var layers = mobile ? [
      { x: '0vw',   y: '0vh',    scale: 1.08, depth: 0.3 },
      { x: '-27vw', y: '-8vh',   scale: 0.62, depth: 0.65 },
      { x: '27vw',  y: '8vh',    scale: 0.58, depth: 0.9 },
      { x: '-22vw', y: '14vh',   scale: 0.46, depth: 1.25 },
      { x: '23vw',  y: '-15vh',  scale: 0.5,  depth: 1.1 },
      { x: '-6vw',  y: '-18vh',  scale: 0.7,  depth: 1.5 },
    ] : HERO_LAYERS;

    wrappers.forEach(function (el, i) {
      var L = layers[i % layers.length];
      gsap.set(el, { x: L.x, y: L.y, scale: L.scale, zIndex: i === 0 ? 1 : i + 2 });
    });

    // Entrance: slow staggered fade, main image first
    tl.from(wrappers, {
      opacity: 0,
      yPercent: 18,
      duration: DUR_SLOW,
      ease: EASE_OUT,
      stagger: 0.1,
    }, 0.6);

    // Scroll parallax: each layer leaves the viewport at its own speed
    wrappers.forEach(function (el, i) {
      var depth = layers[i % layers.length].depth;
      gsap.to(el, {
        yPercent: -34 * depth,
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
      });
    });

    // Mouse drift on the inner images (slightly overscaled so edges never show)
    if (!finePointer) return;
    var drifters = wrappers.map(function (el, i) {
      var img = el.querySelector('.image-hero, .placeholder-img');
      if (!img) return null;
      gsap.set(img, { scale: 1.14 });
      return {
        depth: layers[i % layers.length].depth,
        x: gsap.quickTo(img, 'x', { duration: 1.4, ease: EASE_OUT }),
        y: gsap.quickTo(img, 'y', { duration: 1.4, ease: EASE_OUT }),
      };
    });

    section.addEventListener('mousemove', function (e) {
      var nx = e.clientX / window.innerWidth - 0.5;
      var ny = e.clientY / window.innerHeight - 0.5;
      drifters.forEach(function (d) {
        if (!d) return;
        d.x(nx * 26 * d.depth);
        d.y(ny * 16 * d.depth);
      });
    });
  }

  /* Mobile fallback: slow, gentle crossfade slideshow */
  function initHeroSlideshow(wrappers, tl) {
    var current = 0;

    wrappers.forEach(function (el, i) {
      gsap.set(el, { opacity: i === 0 ? 1 : 0 });
    });

    tl.from(wrappers[0], { opacity: 0, scale: 1.06, duration: DUR_SLOW, ease: EASE_SOFT }, 0.6);

    if (wrappers.length < 2) return;
    setInterval(function () {
      current = (current + 1) % wrappers.length;
      wrappers.forEach(function (el, i) {
        if (i === current) {
          gsap.to(el, { opacity: 1, scale: 1, duration: 1.4, ease: EASE_SOFT });
        } else {
          gsap.to(el, { opacity: 0, scale: 1.04, duration: 1.2, ease: 'power2.in' });
        }
      });
    }, 5000);
  }

  /* ============================================================
     7. SCROLL REVEALS — slow fades and masked rises, play once
  ============================================================ */
  function initReveals() {
    // [selector, y-offset, stagger] — every target ends fully visible
    var groups = [
      ['.heading-content-works > *',            70, 0.12],
      ['.tagline-section-why',                  30, 0],
      ['.text-head-why',                        60, 0.14],
      ['.area-list .list-item-why',             30, 0.08],
      ['.text-contact-cta .cta-text-wrap',      24, 0.1],
      ['.cta-big-text-wrap',                    50, 0],
      ['.footer-heading-wrap',                  60, 0],
      ['.link-footer',                          36, 0.09],
      ['.footer-bar-wrap',                      20, 0],
      ['[data-reveal]',                         50, 0.1],
    ];

    groups.forEach(function (g) {
      var els = gsap.utils.toArray(g[0]);
      if (!els.length) return;
      gsap.from(els, {
        scrollTrigger: {
          trigger: els[0].parentElement || els[0],
          start: 'top 88%',
          once: true,
        },
        opacity: 0,
        y: g[1],
        duration: DUR_SLOW,
        ease: EASE_OUT,
        stagger: g[2],
        clearProps: 'opacity,transform',
      });
    });
  }

  /* ============================================================
     8. IMAGE EXPANSION ON SCROLL
     Frames un-clip while the inner image settles from an
     overscaled state — tied to scroll for a tactile feel.
  ============================================================ */
  function initImageExpansion() {
    var frames = gsap.utils.toArray(
      '.wrapper-img-v2, .outer-wrapp-img-about, .outer-wrapp-img-client, [data-expand]'
    );

    frames.forEach(function (frame) {
      var img = frame.querySelector('img');

      gsap.fromTo(frame,
        { clipPath: 'inset(10% 8% 10% 8%)' },
        {
          clipPath: 'inset(0% 0% 0% 0%)',
          ease: 'none',
          scrollTrigger: {
            trigger: frame,
            start: 'top 96%',
            end: 'top 42%',
            scrub: 0.8,
          },
        }
      );

      if (img) {
        gsap.fromTo(img,
          { scale: 1.18, yPercent: -5 },
          {
            scale: 1,
            yPercent: 5,
            ease: 'none',
            scrollTrigger: {
              trigger: frame,
              start: 'top 100%',
              end: 'bottom 0%',
              scrub: true,
            },
          }
        );
      }
    });
  }

  /* ============================================================
     9. SERVICES SCROLL + HOVER IMAGE SWAP
  ============================================================ */
  function initServiceHover() {
    const serviceWrap = document.querySelector('.service-wrap');
    const serviceItems = document.querySelectorAll('.service-list-content-wrap:not(.mobile)');
    const serviceImages = document.querySelectorAll('.service-images-wrap .outer-wrapp-img');
    if (!serviceItems.length || !serviceImages.length) return;

    let activeIndex = -1;

    serviceImages.forEach((img, i) => {
      img.classList.toggle('is-active', i === 0);
      gsap.set(img, { opacity: i === 0 ? 1 : 0, scale: i === 0 ? 1 : 0.96 });
    });

    function setActive(index, immediate) {
      if (index === activeIndex) return;
      activeIndex = index;

      serviceItems.forEach((item, i) => {
        const active = i === index;
        const line = item.querySelector('.in-lines');
        item.classList.toggle('is-active', active);

        gsap.to(item, {
          opacity: active ? 1 : 0.72,
          x: active ? 0 : 6,
          duration: immediate ? 0 : 0.65,
          ease: EASE_SOFT,
          overwrite: 'auto',
        });
        if (line) {
          gsap.to(line, {
            scaleX: active ? 1 : 0,
            duration: immediate ? 0 : 0.75,
            ease: 'power3.out',
            transformOrigin: 'left',
            overwrite: 'auto',
          });
        }
      });

      serviceImages.forEach((img, i) => {
        const active = i === index;
        img.classList.toggle('is-active', active);
        gsap.to(img, {
          opacity: active ? 1 : 0,
          scale: active ? 1 : 0.96,
          duration: immediate ? 0 : 0.9,
          ease: EASE_OUT,
          overwrite: 'auto',
        });
      });
    }

    setActive(0, true);

    serviceItems.forEach((item, index) => {
      item.addEventListener('mouseenter', () => {
        setActive(index);
      });
    });

    if (!serviceWrap || typeof ScrollTrigger === 'undefined') return;

    ScrollTrigger.create({
      trigger: serviceWrap,
      start: 'top 45%',
      end: 'bottom 55%',
      scrub: true,
      onUpdate: (self) => {
        const index = Math.min(
          serviceItems.length - 1,
          Math.floor(self.progress * serviceItems.length)
        );
        setActive(index);
      },
    });
  }

  /* ============================================================
     10. PORTFOLIO — scrolling text lines, speed/direction tied to scroll
  ============================================================ */
  function initPortfolioLines() {
    const wrap = document.querySelector('.portfolio-lines-wrap');
    if (!wrap) return;

    const tracks = wrap.querySelectorAll('.portfolio-line-track');
    if (!tracks.length || typeof ScrollTrigger === 'undefined') return;

    tracks.forEach((track, i) => {
      const direction = i % 2 === 0 ? -1 : 1;
      const distance = 160 + i * 90; // px — each line travels a different distance

      gsap.to(track, {
        x: direction * distance,
        ease: 'none',
        scrollTrigger: {
          trigger: wrap,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.6,
        },
      });
    });
  }

  /* ============================================================
     11. TESTIMONIALS STICKY SCROLL
  ============================================================ */
  function initTestimonialsScroll() {
    const container = document.querySelector('.content-testimoni-v2');
    if (!container || typeof ScrollTrigger === 'undefined') return;

    const cards = container.querySelectorAll('.sticky-testimonial:not(.null)');
    if (!cards.length) return;

    cards.forEach((card, i) => {
      ScrollTrigger.create({
        trigger: card,
        start: 'top top',
        end: () => `+=${card.offsetHeight}`,
        pin: true,
        pinSpacing: i === cards.length - 1,
      });
    });
  }

  /* ============================================================
     12. CTA MARQUEE GSAP
  ============================================================ */
  function initCtaMarquee() {
    const marquee = document.querySelector('.cta-big-text-marquee');
    if (!marquee || typeof gsap === 'undefined') return;

    // Override CSS animation with GSAP — slow, endless drift
    gsap.to(marquee, {
      xPercent: -33.333,
      ease: 'none',
      duration: 38,
      repeat: -1,
    });
  }

  /* ============================================================
     13. LIGHT / DARK MODE TOGGLE
  ============================================================ */
  function initThemeToggle() {
    const btn = document.getElementById('theme-toggle');
    if (!btn) return;

    const html = document.documentElement;
    const metaThemeColor = document.getElementById('theme-color-meta');

    const STORAGE_KEY = 'cobj-theme';
    const COLORS = { dark: '#0a0a0a', light: '#f5f2ed' };

    // Read current theme (already set by anti-flash script)
    function getTheme() {
      return html.getAttribute('data-theme') || 'dark';
    }

    function applyTheme(theme, animate) {
      html.setAttribute('data-theme', theme);
      localStorage.setItem(STORAGE_KEY, theme);

      // Update browser chrome color
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', COLORS[theme]);
      }

      // Update aria-label
      btn.setAttribute(
        'aria-label',
        theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'
      );

      // GSAP button spin on toggle
      if (animate && typeof gsap !== 'undefined') {
        gsap.fromTo(
          btn,
          { rotate: theme === 'light' ? -30 : 30, scale: 0.85 },
          { rotate: 0, scale: 1, duration: 0.5, ease: 'back.out(1.7)' }
        );
      }
    }

    // Set correct aria-label on load
    applyTheme(getTheme(), false);

    btn.addEventListener('click', function () {
      const next = getTheme() === 'dark' ? 'light' : 'dark';
      applyTheme(next, true);
    });

    // Keyboard: Space / Enter already fire click, also support T shortcut
    document.addEventListener('keydown', function (e) {
      // Shift+T shortcut to toggle (avoids interfering with inputs)
      if (e.shiftKey && e.key === 'T' && document.activeElement.tagName !== 'INPUT') {
        btn.click();
      }
    });
  }

  /* ============================================================
     INIT
  ============================================================ */
  document.addEventListener('DOMContentLoaded', function () {
    initThemeToggle();
    initClock();
    initMenu();
    initLenis();
    initImageTrail();
    // Defer GSAP init slightly to allow GSAP scripts to load
    if (typeof gsap !== 'undefined') {
      initGSAP();
    } else {
      window.addEventListener('load', initGSAP);
    }
  });
})();
